import React, { useEffect, useRef, useState } from 'react';
import { uploadFile } from '../../api/portal';
import FileChip from './FileChip';

/**
 * Composer — the message input for the conversation rail (Epic 7 · S7.3, Part A.6).
 *
 * Hidden when `canWrite === false` (the team-member read-only state, Part G).
 * The caller MUST NOT render this component when `!canWrite` — but it also
 * defensively returns null so a mis-wiring can't surface a box the user can't
 * act on.
 *
 * Behaviour (Part A.6):
 *   - Text box + attach + send.
 *   - Enter sends; Shift+Enter is a newline.
 *   - Attach → `uploadFile(parkId, file)` per file → FileChips with live upload
 *     progress → on send the collected paths are passed to `onSend`.
 *   - Raised messages (Part A.6 validation table) are shown verbatim under the
 *     composer — the user sees the exact string the database raised.
 *
 * `onSend({ body, attachmentPaths })` is a prop so the owning
 * ConversationPanel owns the `sendMessage` call (the single data-access surface).
 * `onUpload` is injectable for tests (defaults to `uploadFile`).
 */
const Composer = ({ canWrite, parkId, onSend, focusComposer, onUpload }) => {
  const ref = useRef(null);
  const fileInput = useRef(null);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);
  const [files, setFiles] = useState([]); // [{ name, size, type, path, progress, uploading }]

  useEffect(() => {
    if (focusComposer) ref.current?.focus();
  }, [focusComposer]);

  if (canWrite === false) return null;

  const attach = async (fileList) => {
    const arr = Array.from(fileList || []);
    for (const file of arr) {
      const id = `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setFiles((s) => [...s, { id, name: file.name, size: file.size, type: file.type, progress: 0, uploading: true }]);
      try {
        const path = await (onUpload || uploadFile)(parkId, file);
        setFiles((s) => s.map((f) => (f.id === id ? { ...f, path, progress: 100, uploading: false } : f)));
      } catch (e) {
        setFiles((s) => s.map((f) => (f.id === id ? { ...f, uploading: false, progress: 100, error: e?.message || 'Upload failed' } : f)));
        setErr(e?.message || 'Upload failed.');
      }
    }
  };

  const send = async () => {
    const text = body.trim();
    if (!text || sending) return;
    // Wait for any upload still in flight so the paths are ready.
    if (files.some((f) => f.uploading)) return;
    setSending(true);
    setErr(null);
    try {
      const paths = files.filter((f) => f.path).map((f) => f.path);
      await onSend?.({ body: text, attachmentPaths: paths });
      setBody('');
      setFiles([]);
    } catch (e) {
      setErr(e?.message || 'Couldn’t send.');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="mt-3 border-t border-gray-100 dark:border-gray-800 pt-3" data-testid="message-composer">
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {files.map((f) => (
            <FileChip key={f.id} file={f} />
          ))}
        </div>
      )}
      <textarea
        ref={ref}
        value={body}
        disabled={sending}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={onKeyDown}
        rows={2}
        placeholder="Write a message…"
        aria-label="Message"
        className="w-full resize-y rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
      />
      <div className="flex items-center justify-between gap-3 mt-2">
        {err ? (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert" data-testid="composer-error">{err}</p>
        ) : (
          <p className="text-xs text-gray-400 dark:text-gray-500">Enter to send · Shift+Enter for a new line</p>
        )}
        <div className="flex items-center gap-2">
          <input
            ref={fileInput}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => { attach(e.target.files); e.target.value = ''; }}
            data-testid="composer-file-input"
          />
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={sending}
            className="inline-flex items-center justify-center min-h-[44px] px-3 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
            aria-label="Attach files"
          >
            Attach
          </button>
          <button
            type="button"
            onClick={send}
            disabled={sending || !body.trim() || files.some((f) => f.uploading)}
            className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-lg text-sm font-medium text-white bg-[#32B1FF] hover:bg-[#1aa3f5] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Composer;
